import { useSort } from "~/pages/inventory/hooks/useSort";
import {
  FindingsOrder,
  FindingsOrderField,
  FindingType,
  OrderDirection,
  ScoreStateFilter,
  useGetAssetPackagesQuery,
  useGetAssetSupportsVulnerabilitiesQuery,
} from "~/operations";
import { CopyTableToClipboardProps } from "~/components/copy-table-to-clipboard";
import { useSoftwareEmptyState } from "~/pages/inventory/components/AssetSoftware/hooks/useSoftwareEmptyState";
import { useAssetOutlet } from "~/pages/inventory/asset";
import { usePaginate } from "~/components/pagination/usePaginate";
import { useAssetSoftwareFilterContext } from "~/pages/inventory/asset/useAssetSoftwareFilterContext";

type UseAssetSoftwareParams = {
  assetMrn: string;
  isVulnerabilityScanningEnabled: boolean;
  clipboardDetails: CopyTableToClipboardProps["tableData"]["details"];
};

export function useAssetSoftware({
  assetMrn,
  isVulnerabilityScanningEnabled,
  clipboardDetails,
}: UseAssetSoftwareParams) {
  const { isCicd } = useAssetOutlet();
  const paginate = usePaginate();
  const baseUrl = paginate.resetCursorUrl(location.href);
  const sort = useSort<FindingsOrder>({
    baseUrl,
    defaultSort: {
      field: FindingsOrderField.RiskValue,
      direction: OrderDirection.Desc,
    },
    validFields: [
      FindingsOrderField.RiskValue,
      FindingsOrderField.PackageName,
      FindingsOrderField.RiskFactors,
      FindingsOrderField.FirstFound,
    ],
  });

  const baseQueryFilters = {
    types: [FindingType.Package],
    includeCicd: isCicd,
    state: ScoreStateFilter.Open,
  };

  const filterContext = useAssetSoftwareFilterContext({
    scopeMrn: assetMrn,
    paginate,
    baseQueryFilters,
  });

  const { loading, data, error } = useGetAssetPackagesQuery({
    variables: {
      scopeMrn: assetMrn,
      filter: {
        ...baseQueryFilters,
        ...filterContext.filterVariables,
      },
      orderBy: sort.orderBy,
      ...paginate.pageVars,
    },
    errorPolicy: "all",
  });

  const { data: assetSupportsData } = useGetAssetSupportsVulnerabilitiesQuery({
    variables: {
      assetMrn,
    },
  });

  const packageScores =
    data?.findings?.__typename === "FindingsConnection"
      ? data.findings
      : undefined;

  const { isEmptyState, emptyStateVariant } = useSoftwareEmptyState({
    packagesConnection: packageScores,
    isFilterApplied: Boolean(filterContext.activeFilterCount > 0),
    isPlatformSupported: Boolean(
      assetSupportsData?.assetSupportVulnerabilities,
    ),
    isVulnerabilityScanningEnabled,
    isNotFound: data?.findings?.__typename === "NotFoundError",
  });

  const tableData: CopyTableToClipboardProps["tableData"] = {
    columns: ["Risk", "Package", "Risk Factors", "First Found"],
    rows: (
      packageScores?.edges?.flatMap((e) =>
        e.node && e.node.__typename === "PackageFinding" ? e.node : [],
      ) || []
    ).map((pkg) => [
      `${pkg.rating} (${pkg.riskValue}/100)`,
      pkg.title + "/" + pkg.installedVersion,
      (
        pkg.riskFactors?.flatMap((rf) =>
          rf && rf.affected > 0 ? rf?.title : "",
        ) ?? []
      ).join(", "),
      pkg.firstFound,
    ]),
    details: clipboardDetails,
  };

  const isScanningEmptyState =
    isEmptyState &&
    emptyStateVariant &&
    ["notFoundError", "platformNotSupported"].includes(emptyStateVariant);

  return {
    isLoading: loading,
    isLoadingFailed: error,
    isScanningEmptyState,
    emptyStateVariant,
    packageScores,
    showCopyToClipboard: packageScores?.totalCount !== 0,
    tableData,
    paginate,
    sort,
    hasData: (packageScores?.totalCount || 0) > 0,
    filterContext,
  };
}
